import axios from 'axios';

// Dynamically determine backend URL based on current domain
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  
  // Production: djnomad.app uses same domain for API
  if (hostname === 'djnomad.app' || hostname.endsWith('.djnomad.app')) {
    return 'https://djnomad.app';
  }
  
  // Emergent preview/host deployments
  if (hostname.endsWith('.preview.emergentagent.com') || hostname.endsWith('.emergent.host')) {
    return `https://${hostname}`;
  }
  
  // Fallback to env variable or same origin
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Create axios instance with HttpOnly cookies support
const api = axios.create({
  baseURL: API,
  withCredentials: true, // ✅ CRITICAL: Required for HttpOnly cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for handling 401 (token refresh)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried and not a refresh/login/register/public request
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/refresh') &&
      !originalRequest.url.includes('/auth/login') &&
      !originalRequest.url.includes('/auth/register') &&
      !originalRequest.url.includes('/public') &&
      !originalRequest.url.includes('/guests/')
    ) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        await axios.post(
          `${API}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        // Retry original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        console.error('Token refresh failed, redirecting to login');
        
        // Clear any user data
        localStorage.removeItem('user');
        
        // Redirect to login
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Handle rate limiting
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || '60';
      console.error(`Rate limit exceeded. Please try again in ${retryAfter} seconds.`);
    }

    return Promise.reject(error);
  }
);

// Auth (now uses HttpOnly cookies)
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const logout = () => api.post('/auth/logout');
export const refreshToken = () => api.post('/auth/refresh');
export const getCurrentUser = () => api.get('/auth/me');

// Events
export const createEvent = (data) => api.post('/events', data);
export const getEvents = () => api.get('/events');
export const getEvent = (id) => api.get(`/events/${id}`);
export const getEventByCode = (code) => api.get(`/events/by-code/${code}`);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data);
export const updateEventStatus = (id, status) => api.patch(`/events/${id}/status`, { status });
export const getEventQR = (id) => api.get(`/events/${id}/qr`);
export const getEventQRCode = (id) => api.get(`/events/${id}/qr`);

// Parts
export const createPart = (data) => api.post('/parts', data);
export const getPart = (id) => api.get(`/parts/${id}`);
export const updatePart = (id, data) => api.put(`/parts/${id}`, data);
export const deletePart = (id) => api.delete(`/parts/${id}`);
export const reorderEventParts = (eventId, order) => api.patch(`/events/${eventId}/parts/order`, order);

// Playlists
export const createPlaylist = (data) => api.post('/playlists', data);
export const getPlaylist = (id) => api.get(`/playlists/${id}`);

// Tracks
export const createTrack = (data) => api.post('/tracks', data);
export const uploadTrack = (formData) => api.post('/tracks/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteTrack = (id) => api.delete(`/tracks/${id}`);
export const reorderTracks = (playlistId, trackIds) => api.put(`/tracks/reorder?playlist_id=${playlistId}`, trackIds);
export const recalculateBPM = (trackId) => api.post(`/tracks/${trackId}/recalculate-bpm`);
export const updateTrackBpm = (trackId, bpm) => api.put(`/tracks/${trackId}`, { bpm, bpm_manually_edited: true });
export const updateTrack = (trackId, data) => api.put(`/tracks/${trackId}`, null, { params: data });

// Track Analysis - Save waveform/BPM to database for instant loading
export const saveTrackAnalysis = (trackId, analysisData) => api.put(`/tracks/${trackId}/analysis`, analysisData);

// PHASE 5: Hot Cues & Loops
export const updateHotCues = (trackId, hotCues) => api.put(`/tracks/${trackId}/hot-cues`, hotCues);
export const updateLoopPoints = (trackId, loopStart, loopEnd, loopEnabled) => 
  api.put(`/tracks/${trackId}/loop`, null, { params: { loop_start: loopStart, loop_end: loopEnd, loop_enabled: loopEnabled } });

// PHASE 8: Track Library Management
export const searchTracks = (params) => api.get('/tracks/search', { params });
export const updateTrackMetadata = (trackId, metadata) => api.put(`/tracks/${trackId}`, metadata);

// Suggestions
export const createSuggestion = (data) => api.post('/suggestions', data);
export const getSuggestions = (eventId) => api.get(`/suggestions/${eventId}`);
export const updateSuggestionStatus = (id, status) => api.patch(`/suggestions/${id}/status?status=${status}`);

// Player
export const getPlayerState = (eventId) => api.get(`/player/${eventId}`);
export const updatePlayerState = (eventId, data) => api.patch(`/player/${eventId}`, data);

// Demo
export const seedDemoData = () => api.post('/demo/seed');

// User Quotas & Limits
export const getUserLimits = () => api.get('/user/limits');
export const getUserQuotas = () => api.get('/user/limits'); // Alias

export default api;
